/**
 * Niyo Jewellé — collection page behaviour (sections/niyo-all-products.liquid)
 * Handles:
 *   • Filter accordions (sidebar + mobile drawer)
 *   • Mobile filter / sort drawers (focus, Escape, swipe to close)
 *   • Checkbox filter, price chip and sort navigation
 *   • Grid density toggle
 *   • "Clear All" (sidebar + drawer)
 *   • Scroll-to-top
 * Filtering uses full page loads so Shopify renders the filtered results.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────
   * UTILITIES
   * ───────────────────────────────────────────────────────────────── */

  function currentUrl() {
    var url = new URL(window.location.href);
    url.searchParams.delete('page'); // reset pagination on any filter / sort change
    return url;
  }

  function navigate(url) {
    window.location.assign(url.toString());
  }

  /* ─────────────────────────────────────────────────────────────────
   * ACCORDION — any [data-filter-group], in the sidebar or drawer
   * ───────────────────────────────────────────────────────────────── */
  function initAccordions() {
    document.querySelectorAll('[data-filter-group]').forEach(function (group) {
      var toggle = group.querySelector('[data-filter-toggle]');
      var body = group.querySelector('[data-filter-body]');
      if (!toggle || !body) return;

      // Open groups start unconstrained; the drawer is display:none at load so
      // measuring scrollHeight here would collapse them to 0.
      if (group.classList.contains('is-open')) {
        body.style.maxHeight = 'none';
        body.style.opacity = '1';
      }

      toggle.addEventListener('click', function () {
        var isOpen = group.classList.contains('is-open');

        if (isOpen) {
          body.style.maxHeight = body.scrollHeight + 'px';
          body.offsetHeight; // eslint-disable-line no-unused-expressions -- force reflow
          body.style.maxHeight = '0';
          body.style.opacity = '0';
          group.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
        } else {
          group.classList.add('is-open');
          toggle.setAttribute('aria-expanded', 'true');
          body.style.maxHeight = body.scrollHeight + 'px';
          body.style.opacity = '1';

          body.addEventListener('transitionend', function onEnd() {
            if (group.classList.contains('is-open')) body.style.maxHeight = 'none';
            body.removeEventListener('transitionend', onEnd);
          });
        }
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────────
   * SORT — sidebar buttons + mobile drawer options
   * ───────────────────────────────────────────────────────────────── */
  function initSort() {
    document.querySelectorAll('[data-sort-value]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var val = btn.getAttribute('data-sort-value');
        var url = currentUrl();
        if (val) url.searchParams.set('sort_by', val);
        else url.searchParams.delete('sort_by');
        navigate(url);
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────────
   * CHECKBOX FILTERS — the sidebar and drawer each render a full copy of
   * every filter, so only read checkboxes from the container that changed.
   * ───────────────────────────────────────────────────────────────── */
  function initCheckboxFilters() {
    document.querySelectorAll('input[type="checkbox"][data-filter-param]').forEach(function (checkbox) {
      checkbox.addEventListener('change', function () {
        var scope = checkbox.closest('[data-filter-scope]') || document;
        var param = checkbox.getAttribute('data-filter-param');
        var url = currentUrl();

        url.searchParams.delete(param);
        scope.querySelectorAll('input[type="checkbox"][data-filter-param]').forEach(function (cb) {
          if (cb.getAttribute('data-filter-param') === param && cb.checked) {
            url.searchParams.append(param, cb.value);
          }
        });

        navigate(url);
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────────
   * PRICE CHIPS — filter.v.price.gte / lte (rupees); click again to remove
   * ───────────────────────────────────────────────────────────────── */
  function initPriceChips() {
    document.querySelectorAll('.niyo-price-chip').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var url = currentUrl();
        var gte = chip.getAttribute('data-price-gte');
        var lte = chip.getAttribute('data-price-lte');
        var isActive = chip.getAttribute('aria-pressed') === 'true';

        url.searchParams.delete('filter.v.price.gte');
        url.searchParams.delete('filter.v.price.lte');
        if (!isActive) {
          if (gte) url.searchParams.set('filter.v.price.gte', gte);
          if (lte) url.searchParams.set('filter.v.price.lte', lte);
        }

        navigate(url);
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────────
   * CLEAR ALL — removes every filter but keeps the chosen sort
   * ───────────────────────────────────────────────────────────────── */
  function initClearAll() {
    document.querySelectorAll('[data-clear-filters]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var url = currentUrl();
        var keys = [];
        url.searchParams.forEach(function (val, key) {
          if (key.indexOf('filter.') === 0) keys.push(key);
        });
        keys.forEach(function (k) { url.searchParams.delete(k); });
        navigate(url);
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────────
   * GRID DENSITY TOGGLE
   * ───────────────────────────────────────────────────────────────── */
  function initGridDensity() {
    var page = document.getElementById('niyo-products-page');
    if (!page) return;
    var buttons = document.querySelectorAll('.niyo-grid-density__btn');

    function apply(cols) {
      page.setAttribute('data-grid', cols);
      buttons.forEach(function (b) {
        var active = b.getAttribute('data-cols') === cols;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var cols = btn.getAttribute('data-cols');
        apply(cols);
        try { localStorage.setItem('niyo_grid', cols); } catch (e) { /* storage unavailable */ }
      });
    });

    try {
      var saved = localStorage.getItem('niyo_grid');
      if (saved && /^[1-4]$/.test(saved)) apply(saved);
    } catch (e) { /* storage unavailable */ }
  }

  /* ─────────────────────────────────────────────────────────────────
   * MOBILE DRAWERS — filter drawer + sort drawer
   * ───────────────────────────────────────────────────────────────── */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function initMobileDrawers() {
    var overlay = document.getElementById('niyo-drawer-overlay');
    var filterDrawer = document.getElementById('niyo-filter-drawer');
    var sortDrawer = document.getElementById('niyo-sort-drawer');
    var filterOpen = document.getElementById('niyo-filter-open');
    var sortOpen = document.getElementById('niyo-sort-open');
    var filterClose = document.getElementById('niyo-drawer-close');
    var sortClose = document.getElementById('niyo-sort-drawer-close');
    var drawerApply = document.getElementById('niyo-drawer-apply');

    var activeDrawer = null;
    var activeTrigger = null;
    var hideTimer = null;

    function onKeydown(event) {
      if (!activeDrawer) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeAll();
        return;
      }
      if (event.key !== 'Tab') return;

      var items = Array.prototype.filter.call(activeDrawer.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function openDrawer(drawer, trigger) {
      if (!drawer || !overlay) return;
      if (activeDrawer && activeDrawer !== drawer) closeAll(true);
      clearTimeout(hideTimer);

      activeDrawer = drawer;
      activeTrigger = trigger;
      overlay.style.display = 'block';
      drawer.style.display = 'flex';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          overlay.classList.add('is-open');
          drawer.classList.add('is-open');
        });
      });
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeydown);

      var closeBtn = drawer.querySelector('.niyo-filter-drawer__close');
      if (closeBtn) closeBtn.focus();
    }

    function closeAll(immediate) {
      if (!overlay || !activeDrawer) return;
      var drawer = activeDrawer;
      var trigger = activeTrigger;
      activeDrawer = null;
      activeTrigger = null;

      overlay.classList.remove('is-open');
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        if (immediate !== true) trigger.focus();
      }

      function hide() {
        drawer.style.display = 'none';
        if (!activeDrawer) overlay.style.display = 'none';
      }
      if (immediate === true) hide();
      else hideTimer = setTimeout(hide, 400);
    }

    if (filterOpen) filterOpen.addEventListener('click', function () { openDrawer(filterDrawer, filterOpen); });
    if (sortOpen) sortOpen.addEventListener('click', function () { openDrawer(sortDrawer, sortOpen); });
    if (filterClose) filterClose.addEventListener('click', closeAll);
    if (sortClose) sortClose.addEventListener('click', closeAll);
    if (overlay) overlay.addEventListener('click', closeAll);
    // Filters apply instantly, so "Show results" just reveals them.
    if (drawerApply) drawerApply.addEventListener('click', closeAll);

    [filterDrawer, sortDrawer].forEach(function (drawer) {
      if (!drawer) return;
      var startY = 0;
      drawer.addEventListener('touchstart', function (e) {
        startY = e.touches[0].clientY;
      }, { passive: true });
      drawer.addEventListener('touchend', function (e) {
        var body = drawer.querySelector('.niyo-filter-drawer__body, .niyo-sort-drawer__body');
        if (body && body.scrollTop > 0) return; // user is scrolling the list
        if (e.changedTouches[0].clientY - startY > 80) closeAll();
      }, { passive: true });
    });
  }

  /* ─────────────────────────────────────────────────────────────────
   * SCROLL TO TOP
   * ───────────────────────────────────────────────────────────────── */
  function initScrollTop() {
    var btn = document.getElementById('niyo-scroll-top');
    if (!btn) return;

    window.addEventListener('scroll', function () {
      btn.classList.toggle('is-visible', window.scrollY > 400);
    }, { passive: true });

    btn.addEventListener('click', function () {
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
      var heading = document.querySelector('.niyo-products-hero__heading');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────────
   * BOOT
   * ───────────────────────────────────────────────────────────────── */
  function init() {
    if (!document.getElementById('niyo-products-page')) return;
    initAccordions();
    initSort();
    initCheckboxFilters();
    initPriceChips();
    initClearAll();
    initGridDensity();
    initMobileDrawers();
    initScrollTop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
